import { isArray, isObject } from 'lodash-es'

import { TypeAheadValue } from '../../../organisms/formBody/types'

interface CanSelectForTest {
  testId?: string
}

export type Item = {
  menuItem_header?: boolean
  menuItem_divider?: boolean
  menuItem_section?: string
  [key: string]: any
}

export enum ValueFetchMode {
  /** Legacy behavior where the label is used as the value */
  LEGACY_LABEL = 'LEGACY_LABEL',
  /** Use the valueKey to fetch the value */
  VALUE = 'VALUE',
  /** Use the item id as the value */
  ID = 'ID',
}
/**
 * - Supports custom options for the TypeAhead component
 * - By default, the TypeAhead supports options that are strings only
 *
 * todo: this config also supports menus, so we should remove references to "typeahead" in this class
 */
export class TypeAheadCustomOptionsConfig<T> {
  options: T[]

  /** Function that renders a custom element to display in the dropdown results menu */
  menuItemRenderer?: (option: T, displayText: JSX.Element, selected?: boolean) => JSX.Element

  /** Function that renders a custom element to display in the input bar once an option has been selected */
  inputLabelRenderer?: (option: T) => JSX.Element

  /** Key of a property on T that gives a display string for T. If omitted, the direct values in options will be displayed  */
  labelKey?: string

  /** Key of a property on T that gives a value string for T. If omitted, the direct values in options will be the value  */
  valueKey?: string

  /** Defines how to fetch the value from the selected item */
  valueFetchMode?: ValueFetchMode

  /** Key of a property on T that gives an id string for T */
  idKey?: string

  constructor(
    options: T[],
    menuItemRenderer?: (option: T, displayText: JSX.Element, selected?: boolean) => JSX.Element,
    inputLabelRenderer?: (option: T) => JSX.Element,
    labelKey?: keyof T & string,
    valueKey?: keyof T & string,
    valueFetchMode?: ValueFetchMode,
    idKey?: keyof T & string,
  ) {
    this.options = options
    this.menuItemRenderer = menuItemRenderer
    this.inputLabelRenderer = inputLabelRenderer
    this.labelKey = labelKey || 'label'
    this.valueKey = valueKey || 'value'
    this.valueFetchMode = valueFetchMode || ValueFetchMode.VALUE
    this.idKey = idKey || 'id'
  }

  getDisplayString(item: T) {
    return this.labelKey ? item[this.labelKey] : item
  }

  getValue(value: TypeAheadValue) {
    if (isArray(value) && isObject(value[0]) && this.valueKey) {
      const valueKey = this.valueKey
      return value.map((val) => val[valueKey])
    }
    return value
  }

  getValueForItem(item: T) {
    if (this.valueFetchMode === ValueFetchMode.LEGACY_LABEL) {
      return this.getDisplayString(item)
    } else if (this.valueFetchMode === ValueFetchMode.ID && this.idKey) {
      return item[this.idKey]
    } else {
      return this.valueKey ? item[this.valueKey] : item
    }
  }

  getLabelForValue(value: string) {
    if (this.valueFetchMode === ValueFetchMode.LEGACY_LABEL) {
      return value
    }
    const option = this.options.find((item) => this.getValueForItem(item) === value)
    return option ? this.getDisplayString(option) : value
  }

  getValueForLabel(label: string) {
    if (this.valueFetchMode === ValueFetchMode.LEGACY_LABEL) {
      return label
    }
    const option = this.options.find((item) => this.getDisplayString(item) === label)
    return option ? String(this.getValueForItem(option)) : label
  }

  // Used to support a section list. Items with these properties will not be rendered as a pressable list item
  getIsPressable<T extends Item>(item: T): boolean {
    return !(item?.menuItem_header || item?.menuItem_divider)
  }

  getOptionTestId<T extends CanSelectForTest>(item: T): string | undefined {
    return item?.testId
  }

  getValueFetchMode(): ValueFetchMode | undefined {
    return this.valueFetchMode
  }
}
